import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import AdminLayout from "./AdminLayout";
import ApiService from "../../services/api";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Package,
  X,
  Check,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

interface Product {
  product_id: number;
  name: string;
  description: string;
  price: number;
  features: string[];
  is_popular: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  image?: string;
}

interface ProductFormData {
  name: string;
  description: string;
  price: string | number;
  features: string[];
  is_popular: boolean;
  image?: string;
}

// Add ImageUpload component before ProductForm component
const ImageUpload = memo(
  ({
    value,
    onChange,
    disabled = false,
  }: {
    value?: string;
    onChange: (base64: string) => void;
    disabled?: boolean;
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string>("");

    const validateAndProcessFile = (file: File) => {
      setError("");

      // Validate file type
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/svg+xml",
      ];
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a PNG, JPEG, or SVG image");
        return;
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        setError("Image size must be less than 2MB");
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        onChange(base64);
      };
      reader.onerror = () => {
        setError("Error reading file");
      };
      reader.readAsDataURL(file);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        validateAndProcessFile(file);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        validateAndProcessFile(file);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
    };

    const removeImage = () => {
      onChange("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    return (
      <div className="space-y-3">
        {!value ? (
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !disabled && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              onChange={handleFileSelect}
              className="hidden"
              disabled={disabled}
            />
            <div className="space-y-2">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  className="h-full w-full"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium text-blue-600 hover:text-blue-500">
                  Click to upload
                </span>{" "}
                or drag and drop
              </div>
              <p className="text-xs text-gray-500">
                PNG, JPEG, SVG up to 2MB (Recommended: 400x300px)
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">
            <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <img
                src={value}
                alt="Product preview"
                // className="w-full h-48 object-cover"
                className="w-full h-48 object-contain"
              />
              <button
                type="button"
                onClick={removeImage}
                disabled={disabled}
                className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => !disabled && fileInputRef.current?.click()}
              disabled={disabled}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
            >
              Change Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              onChange={handleFileSelect}
              className="hidden"
              disabled={disabled}
            />
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    );
  }
);

// Separate ProductForm component to prevent re-renders
const ProductForm = memo(
  ({
    initialData,
    onSubmit,
    onCancel,
    isSubmitting,
    isEditing,
  }: {
    initialData: ProductFormData;
    onSubmit: (data: ProductFormData) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    isEditing: boolean;
  }) => {
    const [formData, setFormData] = useState<ProductFormData>(initialData);

    // Reset form data when initialData changes
    useEffect(() => {
      setFormData(initialData);
    }, [initialData]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    };

    const updateName = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, name: e.target.value }));
    };

    const updateDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, description: e.target.value }));
    };

    const updatePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;

      // allow only digits
      if (!/^\d*$/.test(val)) return;

      // normalize leading zeros (except for single 0)
      if (val.length > 1) {
        val = val.replace(/^0+/, "");
        if (val === "") val = "0";
      }

      setFormData((prev) => ({
        ...prev,
        price: val === "" ? "" : +val,
      }));
    };

    const updatePopular = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, is_popular: e.target.checked }));
    };

    const addFeature = () => {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, ""],
      }));
    };

    const removeFeature = (index: number) => {
      setFormData((prev) => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index),
      }));
    };

    const updateFeature = (index: number, value: string) => {
      setFormData((prev) => {
        const updatedFeatures = [...prev.features];
        updatedFeatures[index] = value;
        return {
          ...prev,
          features: updatedFeatures,
        };
      });
    };

    return (
      <form onSubmit={handleSubmit}>
        <div className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={updateName}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
              placeholder="Enter product name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={updateDescription}
              rows={4}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none text-sm sm:text-base"
              placeholder="Enter product description"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Image
            </label>
            <ImageUpload
              value={formData.image}
              onChange={(base64) =>
                setFormData((prev) => ({ ...prev, image: base64 }))
              }
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-sm sm:text-base">
                $
              </span>
              <input
                type="text"
                value={formData.price}
                onChange={updatePrice}
                min="0"
                step="10"
                className="w-full pl-7 sm:pl-8 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                placeholder="0"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Features (Maximum 6)
            </label>
            <div className="space-y-3">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2 sm:gap-3">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                    placeholder={`Feature ${index + 1}`}
                  />
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {formData.features.length < 6 && (
              <button
                type="button"
                onClick={addFeature}
                className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Feature
              </button>
            )}

            {formData.features.length >= 4 && (
              <p className="text-xs text-gray-500 mt-2">
                Maximum 6 features allowed
              </p>
            )}
          </div>

          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_popular}
                onChange={updatePopular}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">
                Mark as Popular Product
              </span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEditing ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {isEditing ? "Update Product" : "Create Product"}
              </>
            )}
          </button>
        </div>
      </form>
    );
  }
);

interface Product {
  product_id: number;
  name: string;
  description: string;
  price: number;
  features: string[];
  is_popular: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  image?: string;
}

interface ProductFormData {
  name: string;
  description: string;
  price: string | number;
  features: string[];
  is_popular: boolean;
  image?: string;
}

const AdminProducts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isAdd = searchParams.get("is_add") === "true";
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    productId: number;
    currentStatus: boolean;
    productName: string;
  } | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data for the modal
  const [modalFormData, setModalFormData] = useState<ProductFormData>({
    name: "",
    description: "",
    price: 0,
    features: [""],
    is_popular: false,
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [currentPage, debouncedSearchTerm, filterStatus]);

  const fetchProducts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        pageSize: pageSize,
      };

      if (debouncedSearchTerm.trim()) {
        params.search_text = debouncedSearchTerm.trim();
      }

      if (filterStatus !== "all") {
        params.is_active = filterStatus === "active" ? 1 : 0;
      }

      const response = await ApiService.searchProducts(params);

      if (response.success) {
        setProducts(response.data);
        setTotalPages(response.totalPages);
        setTotalItems(response.total);
      } else {
        throw new Error(response.message || "Failed to fetch products");
      }
    } catch (err: any) {
      console.error("Error fetching products:", error);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch products"
      );
      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProduct = useCallback(() => {
    setEditingProduct(null);
    setModalFormData({
      name: "",
      description: "",
      price: 0,
      features: [""],
      is_popular: false,
      image: "",
    });
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (isAdd) {
      handleCreateProduct();
    }
  }, [isAdd]);

  const handleEditProduct = useCallback((product: Product) => {
    setEditingProduct(product);
    setModalFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      features: [...product.features],
      is_popular: product.is_popular,
      image: product.image || "",
    });
    setShowModal(true);
  }, []);

  const handleDeleteProduct = async (
    productId: number,
    currentStatus: boolean,
    productName: string
  ) => {
    setConfirmAction({ productId, currentStatus, productName });
    setShowConfirmModal(true);
  };

  const executeStatusToggle = async () => {
    if (!confirmAction) return;

    const { productId, currentStatus } = confirmAction;
    const action = currentStatus ? "deactivate" : "activate";

    try {
      setIsSubmitting(true);
      await ApiService.deleteProduct({
        product_id: productId,
        action: action,
      });
      fetchProducts();
      setShowConfirmModal(false);
      setConfirmAction(null);
    } catch (err: any) {
      console.error("Error updating product status:", error);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update product status"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (formData: ProductFormData) => {
    // Validate features array (max 4 items, remove empty ones)
    const validFeatures = formData.features.filter(
      (feature) => feature.trim() !== ""
    );
    if (validFeatures.length > 6) {
      setError("Maximum 6 features are allowed");
      return;
    }

    const submitData = {
      ...formData,
      features: validFeatures,
      price: +formData.price,
    };

    try {
      setIsSubmitting(true);
      setError(null);

      if (editingProduct) {
        await ApiService.updateProduct({
          product_id: editingProduct.product_id,
          ...submitData,
        });
      } else {
        await ApiService.createProduct(submitData);
      }

      setShowModal(false);
      fetchProducts();
    } catch (err: any) {
      console.error("Error saving product:", error);
      setError(
        err?.response?.data?.message || err?.message || "Failed to save product"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove the old unused handlers since we now use the separate ProductForm component
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterStatus(e.target.value as "all" | "active" | "inactive");
    },
    []
  );

  // Enhanced Modal Component
  const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = "max-w-2xl",
  }: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: string;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className={`relative transform overflow-hidden rounded-2xl bg-white px-4 py-4 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full ${maxWidth} max-h-[90vh] overflow-y-auto sm:p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 leading-6">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>{children}</div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Confirmation Modal Component
  const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    action,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    action: {
      productId: number;
      currentStatus: boolean;
      productName: string;
    } | null;
  }) => {
    if (!isOpen || !action) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className="relative transform overflow-hidden rounded-2xl bg-white px-4 py-4 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full sm:max-w-md sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start mb-4">
              <div
                className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 ${
                  action.currentStatus ? "bg-red-100" : "bg-green-100"
                }`}
              >
                {action.currentStatus ? (
                  <Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                ) : (
                  <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                )}
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                  {action.currentStatus ? "Deactivate" : "Activate"} Product
                </h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  This action can be reversed later
                </p>
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                Are you sure you want to{" "}
                <span
                  className={`font-semibold ${
                    action.currentStatus ? "text-red-600" : "text-green-600"
                  }`}
                >
                  {action.currentStatus ? "deactivate" : "activate"}
                </span>{" "}
                the product{" "}
                <span className="font-semibold text-gray-900">
                  "{action.productName}"
                </span>
                ?
              </p>

              <div
                className={`p-3 sm:p-4 rounded-lg ${
                  action.currentStatus
                    ? "bg-red-50 border border-red-200"
                    : "bg-green-50 border border-green-200"
                }`}
              >
                <p
                  className={`text-xs sm:text-sm ${
                    action.currentStatus ? "text-red-700" : "text-green-700"
                  }`}
                >
                  {action.currentStatus
                    ? "This product will be hidden from customers and marked as inactive."
                    : "This product will be marked as active, but it must be manually reassigned to customers to become visible."}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center ${
                  action.currentStatus
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {action.currentStatus ? "Deactivating..." : "Activating..."}
                  </>
                ) : (
                  <>
                    {action.currentStatus ? "Deactivate" : "Activate"} Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile-friendly Product Card Component
  const ProductCard = ({ product }: { product: Product }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div
            className={
              product?.image
                ? "h-12 w-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border"
                : "h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"
            }
          >
            {product?.image ? (
              <img
                src={product?.image}
                alt={product.name}
                // className="h-10 w-10"
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-6 w-6 text-blue-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                {product.name}
              </h3>
              {product.is_popular && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Popular
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
              {product.description}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-900">
                ${product.price.toFixed(2)}
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  product.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {product.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Created: {formatDate(product.created_at)}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-3">
          <button
            onClick={() => handleEditProduct(product)}
            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() =>
              handleDeleteProduct(
                product.product_id,
                product.is_active,
                product.name
              )
            }
            className={`p-2 rounded-lg transition-colors ${
              product.is_active
                ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                : "text-green-600 hover:text-green-900 hover:bg-green-50"
            }`}
            title={product.is_active ? "Deactivate" : "Activate"}
          >
            {product.is_active ? (
              <Trash2 className="h-4 w-4" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Products">
      <div className="space-y-4 sm:space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div className="text-sm text-red-700">{error}</div>
              <button
                onClick={() => setError(null)}
                className="text-red-700 hover:text-red-900 ml-4"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Products
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Total {totalItems}
            </p>
          </div>
          <button
            onClick={handleCreateProduct}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterStatus}
                onChange={handleStatusFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Products Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product.product_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className={
                                product?.image
                                  ? "h-10 w-10 bg-white-100 rounded-lg flex items-center justify-center mr-4"
                                  : "h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4"
                              }
                            >
                              {product?.image ? (
                                <img
                                  src={product?.image}
                                  alt={product.name}
                                  // className="h-10 w-10"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Package className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 flex items-center">
                                {product.name}
                                {product.is_popular && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Popular
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {product.description.length > 50
                                  ? `${product.description.substring(0, 50)}...`
                                  : product.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            ${product.price.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              product.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {product.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(product.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteProduct(
                                  product.product_id,
                                  product.is_active,
                                  product.name
                                )
                              }
                              className={`p-1 ${
                                product.is_active
                                  ? "text-red-600 hover:text-red-900"
                                  : "text-green-600 hover:text-green-900"
                              }`}
                              title={
                                product.is_active ? "Deactivate" : "Activate"
                              }
                            >
                              {product.is_active ? (
                                <Trash2 className="h-4 w-4" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Card View */}
              <div className="lg:hidden p-4 space-y-4">
                {products.map((product) => (
                  <ProductCard key={product.product_id} product={product} />
                ))}
              </div>

              {products.length === 0 && !isLoading && (
                <div className="text-center py-12 px-4">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No products found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {debouncedSearchTerm || filterStatus !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Get started by creating your first product."}
                  </p>
                  {!debouncedSearchTerm && filterStatus === "all" && (
                    <button
                      onClick={handleCreateProduct}
                      className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Enhanced Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setConfirmAction(null);
          }}
          onConfirm={executeStatusToggle}
          action={confirmAction}
        />

        {/* Enhanced Product Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingProduct ? "Edit Product" : "Create New Product"}
        >
          <ProductForm
            initialData={modalFormData}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
            isSubmitting={isSubmitting}
            isEditing={!!editingProduct}
          />
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
